// Share lookup requests across navigation without persisting user data to disk.
const entries = new Map();
const MAX_AGE = 5 * 60 * 1000;

export function clearOptionsCache() {
    entries.clear();
}

function forSubscriber(promise, signal) {
    if (!signal) return promise.then(response => response.clone());
    return new Promise((resolve, reject) => {
        const abort = () => reject(new DOMException("Request aborted", "AbortError"));
        if (signal.aborted) { abort(); return; }
        signal.addEventListener("abort", abort, { once: true });
        promise.then(response => {
            if (!signal.aborted) resolve(response.clone());
        }, reject).finally(() => signal.removeEventListener("abort", abort));
    });
}

export default function optionsFetch(input, init = {}) {
    if (init.signal?.aborted) return Promise.reject(new DOMException("Request aborted", "AbortError"));
    const url = new URL(typeof input === "string" ? input : input.url, window.location.origin);
    const method = String(init.method || input.method || "GET").toUpperCase();
    const isApi = /^\/apiv7\//i.test(url.pathname);
    // Saved edits/deletions must be reflected in the next page's lookups.
    if (isApi && !["GET", "HEAD", "OPTIONS"].includes(method)) {
        return fetch(input, init).then(response => {
            if (response.ok && !/^\/apiv7\/userfilters(?:\/|$)/i.test(url.pathname)) clearOptionsCache();
            return response;
        });
    }
    if (method !== "GET" || !/^\/apiv7\/options\/?$/i.test(url.pathname)
        || ["reload", "no-store", "no-cache"].includes(init.cache)) return fetch(input, init);

    url.searchParams.sort();
    const headers = init.headers || input.headers || {};
    const header = name => typeof headers.get === "function" ? headers.get(name)
        : (Array.isArray(headers) ? headers : Object.entries(headers))
            .find(([key]) => key.toLowerCase() === name.toLowerCase())?.[1] || null;
    const key = JSON.stringify([url.origin, url.pathname.toLowerCase(), url.search,
        header("Authorization"), header("Accept-Language")]);
    let entry = entries.get(key);
    if (!entry || entry.expires <= Date.now()) {
        entry = { expires: Infinity, promise: null };
        // A page unmount must not abort a request shared with the sidebar.
        entry.promise = fetch(input, { ...init, signal: undefined }).then(response => {
            if (!response.ok) {
                if (entries.get(key) === entry) entries.delete(key);
            } else entry.expires = Date.now() + MAX_AGE;
            return response;
        }).catch(error => {
            if (entries.get(key) === entry) entries.delete(key);
            throw error;
        });
        entries.set(key, entry);
    }
    return forSubscriber(entry.promise, init.signal);
}
